/**
 * «Позвонить продавцу» на карточке объявления (T18.8).
 *
 * ## Цена — до подтверждения, а не после
 *
 * Требование `02_SPEC.md` §11.1: человек видит, спишут ли что-нибудь, до
 * нажатия. Поэтому кнопка сначала раскрывает подтверждение с пресетом,
 * остатком бесплатных звонков и ориентировочной доплатой, и только оно
 * ставит звонок. Оценка берётся тем же решением, что и сама постановка,
 * — «бесплатно» здесь не обернётся отказом там.
 *
 * ## Отказ — словами сервера
 *
 * Причины отказа сервер называет сам: «телефон продавца пока не
 * получен», «платные пока не открыты». Переписывать их здесь значило бы
 * однажды разойтись с тем, что происходит на самом деле.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiFailure, request } from '../../shared/api';

interface Preset {
  readonly id: string;
  readonly name: string;
  readonly is_system: boolean;
}

interface Estimate {
  readonly free_allowed: number;
  readonly free_left: number;
  readonly next: string;
  readonly minute_minor: number;
  readonly max_seconds: number;
  readonly worst_case_minor: number;
}

function rubles(minor: number): string {
  return `${new Intl.NumberFormat('ru-RU').format(Math.round(minor / 100))} ₽`;
}

/** Что будет со следующим звонком — одной строкой. */
function chargeLine(estimate: Estimate): string {
  switch (estimate.next) {
    case 'included':
      return `Бесплатно: осталось ${String(estimate.free_left)} из ${String(estimate.free_allowed)} по тарифу`;
    case 'balance':
      return `С баланса: ${rubles(estimate.minute_minor)} за минуту, не больше ${rubles(estimate.worst_case_minor)} за звонок`;
    case 'free_ceiling_reached':
      return 'Бесплатные звонки в этом месяце временно закончились';
    case 'paid_calls_closed':
      return 'Бесплатные звонки закончились, платные пока не открыты';
    case 'not_enough_money':
      return `Не хватает средств: нужно до ${rubles(estimate.worst_case_minor)}`;
    default:
      return '';
  }
}

type Stage =
  | { readonly kind: 'closed' }
  | { readonly kind: 'open' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'queued' }
  | { readonly kind: 'failed'; readonly message: string };

export function CallSeller({
  sourceId,
  listingId,
}: {
  sourceId: number;
  listingId: string;
}): React.JSX.Element {
  const [stage, setStage] = useState<Stage>({ kind: 'closed' });
  const [presets, setPresets] = useState<readonly Preset[]>([]);
  const [presetId, setPresetId] = useState('');
  const [estimate, setEstimate] = useState<Estimate | null>(null);

  useEffect(() => {
    if (stage.kind !== 'open') return undefined;

    let alive = true;

    Promise.all([
      request<Preset[]>('/v1/ai-call-presets'),
      request<Estimate>('/v1/ai-calls/estimate'),
    ])
      .then(([found, cost]) => {
        if (!alive) return;
        setPresets(found);
        setPresetId((current) => (current === '' ? (found[0]?.id ?? '') : current));
        setEstimate(cost);
      })
      .catch(() => {
        if (alive) setStage({ kind: 'failed', message: 'Не удалось узнать стоимость звонка' });
      });

    return () => {
      alive = false;
    };
  }, [stage.kind]);

  const call = useCallback(() => {
    setStage({ kind: 'sending' });

    request('/v1/ai-calls', {
      method: 'POST',
      body: { source_id: sourceId, listing_id: listingId, preset_id: presetId },
    })
      .then(() => {
        setStage({ kind: 'queued' });
      })
      .catch((failure: unknown) => {
        setStage({
          kind: 'failed',
          message: failure instanceof ApiFailure ? failure.message : 'Не удалось поставить звонок',
        });
      });
  }, [sourceId, listingId, presetId]);

  if (stage.kind === 'closed') {
    return (
      <button
        type="button"
        className="button card"
        onClick={() => {
          setStage({ kind: 'open' });
        }}
      >
        Позвонить продавцу с помощью AI
      </button>
    );
  }

  if (stage.kind === 'queued') {
    return (
      <p className="notice">
        Звонок поставлен. Итог придёт сообщением от бота и появится в профиле в разделе «Звонки».
      </p>
    );
  }

  if (stage.kind === 'failed') {
    return <p className="notice">{stage.message}</p>;
  }

  const allowed =
    estimate !== null && (estimate.next === 'included' || estimate.next === 'balance');

  return (
    <div className="card">
      <p>
        Звонит AI-помощник Xoreva: представится ассистентом, расспросит о машине и пришлёт отчёт.
      </p>
      <select
        value={presetId}
        onChange={(event) => {
          setPresetId(event.target.value);
        }}
      >
        {presets.map((one) => (
          <option key={one.id} value={one.id}>
            {one.name}
          </option>
        ))}
      </select>
      <p>{estimate === null ? 'Считаем стоимость…' : chargeLine(estimate)}</p>
      <button
        type="button"
        className="button button--accent"
        disabled={!allowed || presetId === '' || stage.kind === 'sending'}
        onClick={call}
      >
        {stage.kind === 'sending' ? 'Ставим в очередь…' : 'Позвонить'}
      </button>{' '}
      <button
        type="button"
        className="button"
        onClick={() => {
          setStage({ kind: 'closed' });
        }}
      >
        Отмена
      </button>
    </div>
  );
}
